import escapeHtml from 'escape-html'
import { CSSProperties } from 'react'
import sanitize from 'sanitize-html'
import { Descendant, Text } from 'slate'
import { Element as SlateElement } from 'slate'
import { jsx } from 'slate-hyperscript'

import { generateId } from 'core-components/helpers/generateId'
import { IExtendedBlock } from 'core-components/library/richTextEditor/interface/IExtendedBlock'

import { DEFAULT_VALUE_FOR_IFRAME, UNUSED_MICROSOFT_TAGS_REGEXP } from '../constants'
import {
  ACTION_FORMATS,
  BLOCK_FORMATS,
  INLINE_FORMATS,
  ISeparatorElement,
  TActionElement,
  TCustomElement,
  TLinkElement,
  TTableCellElement,
} from '../interface/custom-types'
import { LinkButtonColors, TLinkButtonColors, TLinkButtonColorsStyles } from '../interface/Tlink'
import { TSeparatorColors, TSeparatorColorsStyles } from '../interface/TSeparator'

import { getActionIcon, getActionIconColor } from './actionHelper'

const RICH_TEXT_EDITOR_BUTTON = 'RichTextEditor__button'
const RICH_TEXT_EDITOR_SEPARATOR = 'RichTextEditor__separator'

const fzToPixel = {
  xxl: '35px',
  xl: '26px',
  lg: '20px',
  base: '16px',
  sm: '14px',
  xs: '12px',
}

export function pixelsToFz(pixels: string) {
  const preparedValue = +pixels.replace(/px/, '')

  if (preparedValue > 0) {
    if (preparedValue < 13) {
      return 'xs'
    } else if (preparedValue < 15) {
      return 'sm'
    } else if (preparedValue < 18) {
      return 'base'
    } else if (preparedValue < 23) {
      return 'lg'
    } else if (preparedValue < 31) {
      return 'xl'
    } else {
      return 'xxl'
    }
  }

  return 'base'
}

function getColor(
  classList: DOMTokenList,
  arrOfColors: TLinkButtonColorsStyles[] | TSeparatorColorsStyles[]
) {
  const backgroundColor = Array.from(classList).filter(
    (item) => item.indexOf('RichTextEditor__button--') > -1
  )
  let colorValue: TLinkButtonColors | TSeparatorColors | undefined
  if (backgroundColor.length) {
    const currentColor = arrOfColors.filter((item) => item.value === backgroundColor[0])
    if (currentColor.length && currentColor[0].name) {
      colorValue = currentColor[0].name
    }
  }

  return colorValue
}

function kebabCase(value: string) {
  const regex = new RegExp(/[A-Z]/g)
  return value.replace(regex, (v) => `-${v.toLowerCase()}`)
}

function cssToString(style: CSSProperties) {
  const finalResult = Object.keys(style).reduce((accumulator, key) => {
    const cssKey = kebabCase(key)
    const cssValue = style[key]?.replace("'", '')
    return `${accumulator}${cssKey}:${cssValue};`
  }, '')

  return finalResult
}

function getCssString(node: Descendant) {
  const cssProperties: CSSProperties = {}

  if (node['align']) {
    cssProperties.textAlign = node['align']
  }

  if (node['color']) {
    cssProperties.color = node['color']
  }

  if (node['alignment']) {
    cssProperties.textAlign = node['alignment']
  }

  if (node['iconPosition']) {
    cssProperties.textAlign = node['iconPosition']
  }

  if (node['width']) {
    cssProperties.width = node['width']
  }

  if (node['maxWidth']) {
    cssProperties.maxWidth = node['maxWidth']
  }

  if (node['height']) {
    cssProperties.height = node['height']
  }

  if (node['fontSize']) {
    cssProperties.fontSize = fzToPixel[node['fontSize']]
  }

  if (
    node['backgroundColor'] &&
    (node['type'] === BLOCK_FORMATS.separator || node['type'] === BLOCK_FORMATS.tableCell)
  ) {
    cssProperties.backgroundColor = node['backgroundColor']
  }

  if (node['borderColor']) {
    cssProperties.borderColor = node['borderColor']
  }

  if (node['border']) {
    cssProperties.border = node['border']
  }
  if (node['borderTop']) {
    cssProperties.borderTop = node['borderTop']
  }
  if (node['borderBottom']) {
    cssProperties.borderBottom = node['borderBottom']
  }
  if (node['borderLeft']) {
    cssProperties.borderLeft = node['borderLeft']
  }
  if (node['borderRight']) {
    cssProperties.borderRight = node['borderRight']
  }

  if (node['lineHeight']) {
    cssProperties.lineHeight = node['lineHeight']
  }

  if (node['marginLeft']) {
    cssProperties.marginLeft = node['marginLeft']
  }
  if (node['marginRight']) {
    cssProperties.marginRight = node['marginRight']
  }
  if (node['margin']) {
    cssProperties.margin = node['margin']
  }

  const stringStyle = cssToString(cssProperties)

  return stringStyle ? ` style="${stringStyle}"` : ''
}

export function deserialize(el: HTMLElement, extendedBlocks?: IExtendedBlock[]) {
  if (el.nodeType === Node.TEXT_NODE) {
    return el.textContent
  } else if (el.nodeType !== Node.ELEMENT_NODE) {
    return null
  } else if (el.nodeName === 'BR') {
    return '\n'
  }
  if (extendedBlocks) {
    const extendedBlock = extendedBlocks.find((block) => block.conversion.isDeserializeBlock?.(el))

    if (extendedBlock) {
      return extendedBlock.conversion.deserialize?.(el)
    }
  }

  const { nodeName } = el
  const parent = el

  let children: (Descendant | string)[] = Array.from(parent.childNodes)
    .map((item) => deserialize(item as HTMLElement, extendedBlocks))
    .flat()
    .filter((item) => item) as (Descendant | string)[]

  if (children.length === 0) {
    children = [{ text: '' }]
  }

  let customStyles = {}

  if (el.style) {
    Object.keys(STYLE_TO_CUSTOM_TAGS).forEach((customStyle) => {
      if (el.style[customStyle]) {
        const res = STYLE_TO_CUSTOM_TAGS[customStyle](el.style)
        customStyles = { ...customStyles, ...res }
      }
    })
  }

  if (el.nodeName === 'BODY') {
    return jsx('fragment', {}, children)
  }

  if (el.nodeName === 'SECTION' && el.getAttribute('class') === 'action-box') {
    const attrs = ELEMENT_TAGS[nodeName](el)
    // Find old editor action block and remove icon div
    const isOldAction =
      el.getAttribute('class') === 'action-box' &&
      el.firstChild &&
      el.children[0].getAttribute('class') === 'action-box-title'
    if (isOldAction) {
      children.shift()
    }
    return jsx('element', { type: BLOCK_FORMATS.action, ...attrs, ...customStyles }, children)
  }

  if (ELEMENT_TAGS[nodeName]) {
    const attrs = ELEMENT_TAGS[nodeName](el)
    return jsx('element', { ...attrs, ...customStyles }, children)
  }

  if (TEXT_TAGS[nodeName]) {
    // Drop the icons because the i tag has been converted to an italicized text node
    if (nodeName === 'I' && el.getAttribute('class')) return null
    const attrs = TEXT_TAGS[nodeName](el)
    return children.map((child) => {
      if (SlateElement.isElement(child)) {
        return jsx('element', { ...attrs, ...customStyles }, child)
      }
      return jsx('text', { ...attrs, ...customStyles }, child)
    })
  }
  return children
}

function getClassColor(
  arrOfColors: TLinkButtonColorsStyles[],
  color: string | undefined,
  className: string | undefined
) {
  const styleClass = arrOfColors.filter((item) => item.name === color)
  const classColor = ` class="${className + ' ' + styleClass[0].value}"`

  return classColor
}

export function serialize(node: Descendant, extendedBlocks?: IExtendedBlock[]) {
  if (Text.isText(node)) {
    let string = escapeHtml(node.text)
    // We have to handling empty strings for moving cursor beetwen inline nodes
    if (string === '') {
      return '<span></span>'
    }
    string = string.replaceAll('\n', '<br />')

    if (node.bold) {
      string = `<strong>${string}</strong>`
    }
    if (node.underline) {
      string = `<u>${string}</u>`
    }
    if (node.italic) {
      string = `<em>${string}</em>`
    }
    const colorStyle = [
      node.color && `color:${node.color}`,
      node.backgroundColor && `background-color:${node.backgroundColor}`,
    ]
      .filter(Boolean)
      .join(';')
    if (colorStyle) {
      string = `<span style="${colorStyle}">${string}</span>`
    }
    if (node.fontSize) {
      string = `<span style="font-size:${node.fontSize}px">${string}</span>`
    }
    if (node.strikethrough) {
      string = `<del>${string}</del>`
    }
    if (node.superscript) {
      string = `<sup>${string}</sup>`
    }
    if (node.subscript) {
      string = `<sub>${string}</sub>`
    }

    return string
  }

  const children: string = node.children
    .map((n: Descendant) => serialize(n, extendedBlocks))
    .join('')
  const style = getCssString(node)

  if (extendedBlocks) {
    const extendedBlock = extendedBlocks.find((block) => block.type === node.type)
    if (extendedBlock) {
      return extendedBlock.conversion.serialize(node)
    }
  }

  const baseCustomElementNode = node as TCustomElement

  switch (baseCustomElementNode.type) {
    case BLOCK_FORMATS.span:
      return `<span${style}>${children}</span>`
    case BLOCK_FORMATS.blockQuote:
      return `<blockquote${style}>${children}</blockquote>`
    case BLOCK_FORMATS.bulletedList:
      return `<ul${style}>${children}</ul>`
    case BLOCK_FORMATS.headingOne:
      return `<h1${style}>${children}</h1>`
    case BLOCK_FORMATS.headingTwo:
      return `<h2${style}>${children}</h2>`
    case BLOCK_FORMATS.headingThree:
      return `<h3${style}>${children}</h3>`
    case BLOCK_FORMATS.headingFour:
      return `<h4${style}>${children}</h4>`
    case BLOCK_FORMATS.headingFive:
      return `<h5${style}>${children}</h5>`
    case BLOCK_FORMATS.headingSix:
      return `<h6${style}>${children}</h6>`
    case BLOCK_FORMATS.listItem:
      return `<li${style}>${children}</li>`
    case BLOCK_FORMATS.numberedList:
      return `<ol${style}${baseCustomElementNode.start ? ` start="${baseCustomElementNode.start}"` : ''}>${children}</ol>`
    case BLOCK_FORMATS.image:
      return `<img${style}${
        baseCustomElementNode.alt ? ` alt="${escapeHtml(baseCustomElementNode.alt)}"` : ''
      }${
        baseCustomElementNode.caption
          ? ` data-caption="${escapeHtml(baseCustomElementNode.caption)}"`
          : ''
      } src="${escapeHtml(baseCustomElementNode.url)}">`
    case BLOCK_FORMATS.link:
      let additionalData = ''
      let target = ''
      if (baseCustomElementNode.view === 'button') {
        additionalData = getClassColor(
          LinkButtonColors,
          baseCustomElementNode.backgroundColor,
          baseCustomElementNode.className
        )
      }
      target = baseCustomElementNode.openInNewTab ? '_blank' : '_self'
      const targetAttribute = target ? ` target="${target}"` : ''
      const withIcon = baseCustomElementNode.icon
        ? `<i data-type="link" class="${baseCustomElementNode.icon}"></i>`
        : ''
      const isImageLink = baseCustomElementNode.view === 'image' ? 'data-type-image="true"' : ''
      return `<a${style}${additionalData}${targetAttribute} href="${baseCustomElementNode.url}"${isImageLink}>${withIcon}${children}</a>`
    case BLOCK_FORMATS.pre:
      return `<pre${style}>${children}</pre>`
    case BLOCK_FORMATS.code:
      return `<code${style}>${children}</code>`
    case BLOCK_FORMATS.figure:
      return `<figure${style} i-height="${
        baseCustomElementNode.height || DEFAULT_VALUE_FOR_IFRAME
      }">${children}</figure>`
    case BLOCK_FORMATS.video:
      return `<video${style} src="${baseCustomElementNode.url}" height="${
        baseCustomElementNode.height || DEFAULT_VALUE_FOR_IFRAME
      }"></video>`
    case BLOCK_FORMATS.separator:
      return `<hr${style}/>`
    case BLOCK_FORMATS.table:
      const withLayout = baseCustomElementNode.isLayout ? `data-layout="true"` : ''
      return `<table${style} ${withLayout}><tbody>${children}</tbody></table>`
    case BLOCK_FORMATS.tableRow:
      return `<tr>${children}</tr>`
    case BLOCK_FORMATS.tableCell:
      const verticalAlign = baseCustomElementNode.verticalAlign
        ? ` data-vertical-align="${baseCustomElementNode.verticalAlign}"`
        : ''
      return `<td${style}${verticalAlign} colspan=${baseCustomElementNode.colspan} rowspan=${baseCustomElementNode.rowspan}>${children}</td>`
    case BLOCK_FORMATS.iframe:
      const name = baseCustomElementNode.name ? ` data-name="${baseCustomElementNode.name}"` : ''
      return `<iframe src="${baseCustomElementNode.url}" height="${
        baseCustomElementNode.height || DEFAULT_VALUE_FOR_IFRAME
      }"${name}></iframe>`
    case BLOCK_FORMATS.action:
      const icon = baseCustomElementNode.format && getActionIcon(baseCustomElementNode.format)
      const variant = baseCustomElementNode.format
        ? baseCustomElementNode.format
        : ACTION_FORMATS.info
      const iconColor =
        baseCustomElementNode.format && getActionIconColor(baseCustomElementNode.format)
      return `<section${style} class="RichTextEditor__action" type="${variant}"><i class="RichTextEditor__action__icon ${iconColor} ${icon}"></i>${children}</section>`
    case BLOCK_FORMATS.expandBlock:
      return `<details${style}>${children}</details>`
    case BLOCK_FORMATS.expandBlockTitle:
      return `<summary>${children}</summary>`
    default:
      return `<p${style}>${children}</p>`
  }
}

export const CUSTOM_TAGS_TO_STYLE = {
  align: (el: TCustomElement) => ({ textAlign: el['align'] }),
  maxWidth: (el: TCustomElement) => ({ maxWidth: el['maxWidth'] }),
  margin: (el: TCustomElement) => ({ margin: el['margin'] }),
  marginLeft: (el: TCustomElement) => ({ marginLeft: el['marginLeft'] }),
  marginRight: (el: TCustomElement) => ({ marginRight: el['marginRight'] }),
  fontSize: (el: TCustomElement) => ({ fontSize: el['fontSize'] }),
  width: (el: TCustomElement) => ({ width: el['width'] }),
  whiteSpace: (el: TCustomElement) => ({ whiteSpace: el['whiteSpace'] }),
  color: (el: TCustomElement) => ({ color: el['color'] }),
  backgroundColor: (el: TCustomElement) => ({ backgroundColor: el['backgroundColor'] }),
  border: (el: TCustomElement) => ({ border: el['border'] }),
  borderTop: (el: TCustomElement) => ({ borderTop: el['borderTop'] }),
  borderBottom: (el: TCustomElement) => ({ borderBottom: el['borderBottom'] }),
  borderLeft: (el: TCustomElement) => ({ borderTop: el['borderTop'] }),
  borderRight: (el: TCustomElement) => ({ borderRight: el['borderRight'] }),
  lineHeight: (el: TCustomElement) => ({ lineHeight: el['lineHeight'] }),
}

export const STYLE_TO_CUSTOM_TAGS = {
  textAlign: (style: CSSStyleDeclaration) => ({ align: style.textAlign }),
  maxWidth: (style: CSSStyleDeclaration) => ({ maxWidth: style.maxWidth }),
  margin: (style: CSSStyleDeclaration) => ({ margin: style.margin }),
  marginLeft: (style: CSSStyleDeclaration) => ({ marginLeft: style.marginLeft }),
  marginRight: (style: CSSStyleDeclaration) => ({ marginRight: style.marginRight }),
  width: (style: CSSStyleDeclaration) => ({ width: style.width }),
  whiteSpace: (style: CSSStyleDeclaration) => ({ whiteSpace: style.whiteSpace }),
  color: (style: CSSStyleDeclaration) => ({ color: style.color }),
  backgroundColor: (style: CSSStyleDeclaration) => ({ backgroundColor: style.backgroundColor }),
  border: (style: CSSStyleDeclaration) => ({ border: style.border }),
  borderTop: (style: CSSStyleDeclaration) => ({ borderTop: style.borderTop }),
  borderBottom: (style: CSSStyleDeclaration) => ({ borderBottom: style.borderBottom }),
  borderLeft: (style: CSSStyleDeclaration) => ({ borderLeft: style.borderLeft }),
  borderRight: (style: CSSStyleDeclaration) => ({ borderRight: style.borderRight }),
  lineHeight: (style: CSSStyleDeclaration) => ({ lineHeight: style.lineHeight }),
}

export const ELEMENT_TAGS = {
  A: (el: HTMLElement) => {
    const openInNewTab = el.getAttribute('target') ? el.getAttribute('target') === '_blank' : true

    if (el.className.indexOf(RICH_TEXT_EDITOR_BUTTON) > -1) {
      const colorValue = getColor(el.classList, LinkButtonColors)

      const iconEl = Array.from(el.getElementsByTagName('i')).find(
        (item) => item.getAttribute('data-type') === 'link'
      )
      const iconString = iconEl?.getAttribute('class')

      const iconPosition = el.style.textAlign
      const fontSize = pixelsToFz(el.style.fontSize)

      const element: TLinkElement = {
        type: BLOCK_FORMATS.link,
        backgroundColor: colorValue,
        url: el.getAttribute('href') || '',
        className: RICH_TEXT_EDITOR_BUTTON,
        view: 'button',
        openInNewTab: openInNewTab ?? true,
        fontSize: fontSize || 'base',
        iconPosition: iconPosition || 'start',
        icon: iconString || '',
        children: [],
      }

      return element
    }

    const element: TLinkElement = {
      type: BLOCK_FORMATS.link,
      url: el.getAttribute('href') || '',
      openInNewTab: openInNewTab ?? true,
      view: el.getAttribute('data-type-image') ? 'image' : 'text',
      children: [],
    }

    return element
  },
  BLOCKQUOTE: () => ({ type: BLOCK_FORMATS.blockQuote }),
  H1: () => ({ type: BLOCK_FORMATS.headingOne }),
  H2: () => ({ type: BLOCK_FORMATS.headingTwo }),
  H3: () => ({ type: BLOCK_FORMATS.headingThree }),
  H4: () => ({ type: BLOCK_FORMATS.headingFour }),
  H5: () => ({ type: BLOCK_FORMATS.headingFive }),
  H6: () => ({ type: BLOCK_FORMATS.headingSix }),
  IMG: (el: HTMLElement) => {
    return {
      type: BLOCK_FORMATS.image,
      url: el.getAttribute('src'),
      alt: el.getAttribute('alt'),
      caption: el.getAttribute('data-caption'),
    }
  },
  LI: (el: HTMLElement) => ({
    type: BLOCK_FORMATS.listItem,
    marginLeft: el.style.marginLeft,
  }),
  OL: (el: HTMLElement) => ({
    type: BLOCK_FORMATS.numberedList,
    start: el.getAttribute('start') || '1',
  }),
  P: () => ({ type: BLOCK_FORMATS.paragraph }),
  PRE: () => ({ type: BLOCK_FORMATS.pre }),
  CODE: () => ({ type: BLOCK_FORMATS.code }),
  UL: () => ({ type: BLOCK_FORMATS.bulletedList }),
  FIGURE: (el: HTMLElement) => ({
    type: BLOCK_FORMATS.figure,
    height: el.getAttribute('i-height'),
  }),
  IFRAME: (el: HTMLElement) => ({
    type: BLOCK_FORMATS.iframe,
    url: el.getAttribute('src'),
    height: el.getAttribute('height'),
    name: el.getAttribute('data-name'),
  }),
  OEMBED: (el: HTMLElement) => ({ type: BLOCK_FORMATS.iframe, url: el.getAttribute('url') }),
  VIDEO: (el: HTMLElement) => ({
    type: BLOCK_FORMATS.video,
    height: el.getAttribute('height'),
    url: el.getAttribute('src'),
  }),
  HR: (el: HTMLElement) => {
    const element: ISeparatorElement = {
      type: BLOCK_FORMATS.separator,
      className: RICH_TEXT_EDITOR_SEPARATOR,
      backgroundColor: el.style.backgroundColor,
      height: el.style.height,
      width: el.style.width,
      alignment: el.style.textAlign,
      children: [],
    }

    return element
  },
  TABLE: (el: HTMLElement) => ({
    type: BLOCK_FORMATS.table,
    isLayout: el.getAttribute('data-layout'),
  }),
  TR: () => ({ type: BLOCK_FORMATS.tableRow }),
  TD: (el: HTMLElement) =>
    ({
      type: BLOCK_FORMATS.tableCell,
      width: el.style.width,
      colspan: Number(el.getAttribute('colspan')) || 1,
      rowspan: Number(el.getAttribute('rowspan')) || 1,
      verticalAlign: el.getAttribute('data-vertical-align'),
      backgroundColor: el.style.backgroundColor,
      borderColor: el.style.borderColor,
      key: generateId('cell'),
      children: [],
    }) as TTableCellElement,
  SECTION: (el: HTMLElement) => {
    // Old RichEditor consisted of blocks of sections, the new consists of paragraphs.
    // Convert everything that is not action block to paragraph.
    const isOldAction = el.getAttribute('class') === 'action-box'
    let format = el.getAttribute('type') as ACTION_FORMATS
    // // Find old editor action block and get block type
    if (isOldAction && el.firstChild) {
      const actionTitle = el.getElementsByClassName('action-box-title')[0]
      format = actionTitle?.getAttribute('type') as ACTION_FORMATS
    }
    return {
      type: BLOCK_FORMATS.action,
      format,
    } as TActionElement
  },
  DETAILS: () => ({
    isOpen: false,
    type: BLOCK_FORMATS.expandBlock,
  }),
  SUMMARY: () => ({
    type: BLOCK_FORMATS.expandBlockTitle,
  }),
}

export const TEXT_TAGS = {
  B: () => ({ bold: true }),
  DEL: () => ({ strikethrough: true }),
  EM: () => ({ italic: true }),
  I: () => ({ italic: true }),
  S: () => ({ strikethrough: true }),
  STRONG: () => ({ bold: true }),
  U: () => ({ underline: true }),
  SPAN: (el: HTMLElement) => {
    const spanAttributes = {
      backgroundColor: el.style.backgroundColor,
      color: el.style.color,
      marginLeft: el.style.marginLeft,
    }
    // when copying from Word we get fontWeight = bold for bold text, but when copying
    // from Google docs the bold text is indicated as fontWeight = 700
    const isBold = el.style.fontWeight === INLINE_FORMATS.bold || el.style.fontWeight === '700'
    const isItalic = el.style.fontStyle === INLINE_FORMATS.italic

    const textDecorationFormats = el.style.textDecoration.split(' ')
    const isUnderline = textDecorationFormats.includes(INLINE_FORMATS.underline)
    const isStrikethrough = textDecorationFormats.includes('line-through')
    const isSuperscript = el.style.verticalAlign === 'super'
    const isSubscript = el.style.verticalAlign === 'sub'
    // inline font size → numeric fontSize mark, px only. parseInt would misread pt/%/em/rem as px
    // and silently rewrite the size on round-trip, so accept px only (mirrors the element path's
    // pixelsToFz px guard). Non-px and unset are skipped (fontSize stays NaN → falsy).
    const rawFontSize = el.style.fontSize
    const fontSize = rawFontSize.endsWith('px') ? parseInt(rawFontSize, 10) : NaN

    return {
      ...spanAttributes,
      ...(fontSize && { fontSize }),
      ...(isUnderline && { underline: true }),
      ...(isBold && { bold: true }),
      ...(isItalic && { italic: true }),
      ...(isStrikethrough && { strikethrough: true }),
      ...(isSuperscript && { superscript: true }),
      ...(isSubscript && { subscript: true }),
    }
  },
  SUP: () => ({ superscript: true }),
  SUB: () => ({ subscript: true }),
}

export const minifyHtml = (s: string) => {
  // We use exceptions for the em|strong|span|u|sup|sub|del tags because the space between
  // these tags may be part of the markup intended by the user,
  // but without this processing, it will be removed by the minification function
  return s
    .replace(/(?<!<\/?(?:em|strong|span|u(?!l)|sup|sub|del|a)[^>]*?)>[\r\n ]+</g, '><')
    .replace(/(<.*?>)|\s+/g, (m, $1) => ($1 ? $1 : ' '))
    .trim()
}

const isHtml = (value: string) => {
  return value.startsWith('<')
}

// it`s need to convert the string to html if an invalid value is received
const prepareValue = (value: string) => {
  const isHtmlValue = isHtml(value)

  if (!isHtmlValue) {
    const sanitezedValue = `<p>${sanitize(value, {
      allowedTags: [],
      allowedAttributes: {},
    })}</p>`

    return sanitezedValue
  }

  return value.replace(UNUSED_MICROSOFT_TAGS_REGEXP, '')
}

export const parseHtmlToValue = ({
  value,
  withoutMinify,
  extendedBlocks,
}: {
  value: string
  withoutMinify?: boolean
  extendedBlocks?: IExtendedBlock[]
}): Descendant[] => {
  const preparedValue = prepareValue(value)

  const minifiedHtml = minifyHtml(preparedValue)
  const parsedDoc = new DOMParser().parseFromString(
    withoutMinify ? preparedValue : minifiedHtml,
    'text/html'
  )
  const htmlDeserialized = deserialize(parsedDoc.body, extendedBlocks)

  return htmlDeserialized as Descendant[]
}
